import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { FirebaseService } from '../../services/firebaseService';
import { EquiposService } from '../../services/equiposService';
import { InventarioService } from '../../services/inventarioService';
import { Cliente, Producto, TipoMovimiento, Equipo } from '../../types';
import { SignatureCapture } from './SignatureCapture';
import { 
  Package, 
  Users, 
  MapPin, 
  Check, 
  CheckCircle2, 
  Plus, 
  Minus, 
  Loader2, 
  ArrowLeft,
  PenTool,
  Wrench,
  X,
  CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';
import { sanitizeForFirestore } from '../../utils/firestoreHelpers';

export const EntregaFlow: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clienteIdParam = searchParams.get('clienteId');

  // Estados de datos
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Estados de la entrega
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [cantidades, setCantidades] = useState<Record<string, number>>({});
  const [tipoPago, setTipoPago] = useState<'no_pagado' | 'pagado_completo' | 'pago_parcial'>('no_pagado');
  const [medioPago, setMedioPago] = useState<'efectivo' | 'transferencia' | 'tarjeta'>('efectivo');
  const [montoPagoParcial, setMontoPagoParcial] = useState<number>(0);
  const [observaciones, setObservaciones] = useState('');

  // Búsqueda de clientes
  const [searchClienteTerm, setSearchClienteTerm] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);

  // Estados post-confirmación
  const [createdEntregaId, setCreatedEntregaId] = useState<string | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showEquiposSection, setShowEquiposSection] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);

  // Estados de gestión de equipos en el éxito
  const [equiposCliente, setEquiposCliente] = useState<Equipo[]>([]);
  const [equiposDisponibles, setEquiposDisponibles] = useState<Equipo[]>([]);
  const [searchEquipoTerm, setSearchEquipoTerm] = useState('');
  const [loadingEquipos, setLoadingEquipos] = useState(false);

  // Selección de cliente
  const handleSelectCliente = useCallback((cliente: Cliente, currentProductos = productos) => {
    setSelectedCliente(cliente);
    setShowClienteDropdown(false);
    
    // Auto-completar cantidades por defecto de la ficha del cliente
    const defaultQtys: Record<string, number> = {};
    currentProductos.forEach(p => {
      const nombreNorm = p.nombre.toLowerCase();
      if ((nombreNorm.includes('10l') || p.envaseCapacidad === 10) && cliente.bidones10) {
        defaultQtys[p.id!] = cliente.bidones10;
      } else if ((nombreNorm.includes('20l') || p.envaseCapacidad === 20) && cliente.bidones20) {
        defaultQtys[p.id!] = cliente.bidones20;
      } else if ((nombreNorm.includes('soda') || nombreNorm.includes('sifón')) && cliente.sodas) {
        defaultQtys[p.id!] = cliente.sodas;
      } else {
        defaultQtys[p.id!] = 0;
      }
    });
    setCantidades(defaultQtys);
    setTipoPago('no_pagado');
    setMontoPagoParcial(0);
    setObservaciones('');
  }, [productos]);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [clientesData, productosData] = await Promise.all([
          FirebaseService.getCollection<Cliente>('clientes'),
          FirebaseService.getCollection<Producto>('productos')
        ]);
        
        setClientes(clientesData);
        const activeProds = productosData.filter(p => p.activo !== false);
        setProductos(activeProds);

        // Si viene cliente por Query Param, pre-seleccionarlo
        if (clienteIdParam) {
          const matched = clientesData.find(c => c.id === clienteIdParam);
          if (matched) {
            setSelectedCliente(matched);
            
            // Auto-completar cantidades por defecto de la ficha del cliente
            const defaultQtys: Record<string, number> = {};
            activeProds.forEach(p => {
              const nombreNorm = p.nombre.toLowerCase();
              if ((nombreNorm.includes('10l') || p.envaseCapacidad === 10) && matched.bidones10) {
                defaultQtys[p.id!] = matched.bidones10;
              } else if ((nombreNorm.includes('20l') || p.envaseCapacidad === 20) && matched.bidones20) {
                defaultQtys[p.id!] = matched.bidones20;
              } else if ((nombreNorm.includes('soda') || nombreNorm.includes('sifón')) && matched.sodas) {
                defaultQtys[p.id!] = matched.sodas;
              } else {
                defaultQtys[p.id!] = 0;
              }
            });
            setCantidades(defaultQtys);
            setTipoPago('no_pagado');
            setMontoPagoParcial(0);
            setObservaciones('');
          }
        }
      } catch (error) {
        console.error('Error al inicializar datos:', error);
        toast.error('Error al cargar clientes o productos');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [clienteIdParam]);

  // Filtrado de clientes para buscador
  const clientesFiltrados = useMemo(() => {
    const esClienteDeHoy = (cliente: Cliente) => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const dayOfMonth = today.getDate();
      const weekOfYear = Math.ceil(dayOfMonth / 7);
      const daysMap: Record<number, string> = {
        0: 'domingo', 1: 'lunes', 2: 'martes', 3: 'miércoles',
        4: 'jueves', 5: 'viernes', 6: 'sábado'
      };
      const todayName = daysMap[dayOfWeek];
      
      const diasVisita = cliente.diasVisita || (cliente.diaVisita ? [cliente.diaVisita] : []);
      const matchesDay = diasVisita.includes(todayName as any);
      if (!matchesDay) return false;
      
      switch (cliente.frecuenciaVisita) {
        case 'semanal': return true;
        case 'quincenal': return weekOfYear % 2 === 1;
        case 'mensual': return dayOfMonth <= 7;
        default: return false;
      }
    };

    let filtered = clientes;
    if (searchClienteTerm) {
      const term = searchClienteTerm.toLowerCase();
      filtered = clientes.filter(c => 
        c.nombre.toLowerCase().includes(term) || 
        c.direccion.toLowerCase().includes(term)
      );
    }

    return [...filtered].sort((a, b) => {
      const aEsHoy = esClienteDeHoy(a);
      const bEsHoy = esClienteDeHoy(b);
      if (aEsHoy && !bEsHoy) return -1;
      if (!aEsHoy && bEsHoy) return 1;
      return a.nombre.localeCompare(b.nombre);
    });
  }, [clientes, searchClienteTerm]);

  // Gestión de cantidades
  const ajustarCantidad = (productoId: string, delta: number, stockDisponible?: number) => {
    setCantidades(prev => {
      const current = prev[productoId] || 0;
      const next = Math.max(0, current + delta);
      if (stockDisponible !== undefined && next > stockDisponible) {
        toast.error(`Stock insuficiente en vehículo (Disponible: ${stockDisponible})`);
        return prev;
      }
      return { ...prev, [productoId]: next };
    });
  };

  // Cálculos de totales
  const totalProductos = useMemo(() => {
    return Object.entries(cantidades).reduce((sum, [prodId, qty]) => {
      const prod = productos.find(p => p.id === prodId);
      return sum + (prod ? prod.precioVenta * qty : 0);
    }, 0);
  }, [cantidades, productos]);

  const totalCobro = totalProductos;

  const montoPagado = useMemo(() => {
    if (tipoPago === 'pagado_completo') return totalCobro;
    if (tipoPago === 'pago_parcial') return montoPagoParcial;
    return 0;
  }, [tipoPago, totalCobro, montoPagoParcial]);

  const nuevoSaldo = useMemo(() => {
    const saldoAnterior = selectedCliente?.saldoPendiente || 0;
    return saldoAnterior + totalCobro - montoPagado;
  }, [selectedCliente, totalCobro, montoPagado]);

  // Confirmar Entrega (Escritura Offline-First)
  const handleConfirmarEntrega = async () => {
    if (!selectedCliente) {
      toast.error('Por favor selecciona un cliente');
      return;
    }

    const productosSeleccionados = Object.entries(cantidades)
      .filter(([, qty]) => qty > 0)
      .map(([prodId, qty]) => {
        const prod = productos.find(p => p.id === prodId)!;
        return {
          productoId: prodId,
          nombre: prod.nombre,
          cantidad: qty,
          precio: prod.precioVenta,
          subtotal: prod.precioVenta * qty
        };
      });

    if (productosSeleccionados.length === 0) {
      toast.error('Debes agregar al menos un producto a la entrega');
      return;
    }

    try {
      setSubmitting(true);

      const entregaData = {
        clienteId: selectedCliente.id || '',
        fecha: new Date(),
        productos: productosSeleccionados,
        envases: {
          entregados: {}, // Mapeado básico si corresponde
          devueltos: {}
        },
        pagos: {
          tipoPago,
          montoPagado,
          medioPago: tipoPago !== 'no_pagado' ? medioPago : null
        },
        total: totalCobro,
        saldoAnterior: selectedCliente.saldoPendiente || 0,
        saldoNuevo: nuevoSaldo,
        observaciones: observaciones,
        pagado: tipoPago === 'pagado_completo',
        createdAt: new Date()
      };

      // Guardar entrega en Firestore (Offline-first)
      const sanitized = sanitizeForFirestore(entregaData);
      const entregaId = await FirebaseService.createDocument('entregas', sanitized as Record<string, unknown>);

      // Descontar inventario localmente (comportamiento optimista)
      for (const item of productosSeleccionados) {
        try {
          await InventarioService.registrarMovimiento(
            item.productoId,
            TipoMovimiento.SALIDA,
            item.cantidad,
            `Entrega a ${selectedCliente.nombre}`,
            user?.uid || 'sistema',
            'Entrega registrada',
            'Disminución automática de stock por entrega'
          );
        } catch (err) {
          console.warn('Error registrando movimiento de inventario:', err);
        }
      }

      // Actualizar cliente localmente (Saldo)
      if (nuevoSaldo !== (selectedCliente.saldoPendiente || 0)) {
        await FirebaseService.updateDocument('clientes', selectedCliente.id!, {
          saldoPendiente: nuevoSaldo,
          ultimaEntregaFecha: new Date()
        });
      }

      setCreatedEntregaId(entregaId);
      toast.success('¡Entrega registrada!');
      
      // Cargar lista de equipos para la fase de éxito
      loadEquiposExito(selectedCliente.id!);
    } catch (error) {
      console.error('Error al registrar la entrega:', error);
      toast.error('Error al confirmar la entrega. Reintente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Cargar equipos asignados y disponibles
  const loadEquiposExito = async (clienteId: string) => {
    try {
      setLoadingEquipos(true);
      const [todosEquipos, asignados] = await Promise.all([
        EquiposService.getEquipos(),
        EquiposService.getEquipos({ clienteId })
      ]);
      
      setEquiposCliente(asignados);
      // Disponibles: Sin cliente asociado
      setEquiposDisponibles(todosEquipos.filter(e => !e.clienteId));
    } catch (error) {
      console.error('Error cargando equipos:', error);
    } finally {
      setLoadingEquipos(false);
    }
  };

  // Asignar/Instalar equipo
  const handleInstalarEquipo = async (equipo: Equipo) => {
    if (!selectedCliente?.id) return;
    try {
      setLoadingEquipos(true);
      await EquiposService.asignarEquipoACliente(equipo.id!, selectedCliente.id, 'Entregado en visita');
      toast.success(`Equipo ${equipo.numeroSerie} instalado`);
      await loadEquiposExito(selectedCliente.id);
    } catch (error) {
      toast.error('Error al instalar el equipo');
      console.error(error);
    } finally {
      setLoadingEquipos(false);
    }
  };

  // Retirar equipo
  const handleRetirarEquipo = async (equipo: Equipo) => {
    if (!selectedCliente?.id) return;
    try {
      setLoadingEquipos(true);
      await EquiposService.retirarEquipoDeCliente(equipo.id!);
      toast.success(`Equipo ${equipo.numeroSerie} retirado`);
      await loadEquiposExito(selectedCliente.id);
    } catch (error) {
      toast.error('Error al retirar el equipo');
      console.error(error);
    } finally {
      setLoadingEquipos(false);
    }
  };

  // Guardar firma digital
  const handleSaveSignature = async (dataUrl: string | null) => {
    if (!createdEntregaId) return;
    try {
      setSignatureData(dataUrl);
      await FirebaseService.updateDocument('entregas', createdEntregaId, {
        firmaURL: dataUrl || ''
      });
      toast.success('Firma digital guardada correctamente');
      setShowSignatureModal(false);
    } catch (error) {
      console.error('Error al guardar firma:', error);
      toast.error('Error al registrar la firma');
    }
  };

  const handleFinalizar = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  // PANTALLA DE ÉXITO POST-CONFIRMACIÓN
  if (createdEntregaId) {
    return (
      <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-3xl p-6 border border-gray-200 dark:border-gray-700 shadow-xl space-y-6">
        <div className="text-center space-y-2 py-4">
          <div className="inline-flex p-3 bg-green-100 dark:bg-green-950/40 rounded-full text-green-600 dark:text-green-400 mb-2">
            <CheckCircle2 className="h-14 w-14" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white">¡Entrega Confirmada!</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            La entrega ha sido guardada. Si estás sin señal, se sincronizará automáticamente.
          </p>
        </div>

        {/* Resumen rápido */}
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-2xl space-y-2 text-sm border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Cliente</span>
            <span className="font-semibold text-gray-900 dark:text-white">{selectedCliente?.nombre}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Total</span>
            <span className="font-semibold text-gray-900 dark:text-white">${totalCobro.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Cobrado ({tipoPago === 'no_pagado' ? 'Fiado' : medioPago})</span>
            <span className="font-semibold text-gray-900 dark:text-white">${montoPagado.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2 font-medium">
            <span className="text-gray-700 dark:text-gray-300">Nuevo Saldo Cliente</span>
            <span className="text-blue-600 dark:text-blue-400">${nuevoSaldo.toLocaleString()}</span>
          </div>
        </div>

        {/* Acciones Opcionales */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Acciones adicionales (Opcional)
          </h3>
          
          {/* Firma Digital */}
          <button
            onClick={() => setShowSignatureModal(true)}
            className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${
              signatureData 
                ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <PenTool className="h-5 w-5" />
              <div className="text-left">
                <p className="font-bold text-sm">Firma Digital del Cliente</p>
                <p className="text-xs opacity-80">{signatureData ? '✓ Firma capturada' : 'Tocar para firmar en pantalla'}</p>
              </div>
            </div>
            {signatureData && <Check className="h-5 w-5" />}
          </button>

          {/* Gestión de Equipos */}
          <button
            onClick={() => setShowEquiposSection(!showEquiposSection)}
            className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-800 dark:text-gray-200 hover:bg-gray-50 transition-all"
          >
            <div className="flex items-center space-x-3">
              <Wrench className="h-5 w-5" />
              <div className="text-left">
                <p className="font-bold text-sm">Gestionar Comodato / Equipos</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Instalar o retirar dispensers</p>
              </div>
            </div>
          </button>

          {/* Sub-sección de Equipos */}
          {showEquiposSection && (
            <div className="p-4 bg-gray-600/20 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-4">
              {loadingEquipos ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {/* Equipos Instalados Actualmente */}
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">Equipos en domicilio ({equiposCliente.length})</h4>
                    {equiposCliente.length === 0 ? (
                      <p className="text-xs text-gray-500">No hay equipos instalados en este cliente.</p>
                    ) : (
                      <div className="space-y-2">
                        {equiposCliente.map(eq => (
                          <div key={eq.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg text-xs border border-gray-200">
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">{eq.marca} {eq.modelo}</p>
                              <p className="text-[10px] text-gray-500">S/N: {eq.numeroSerie}</p>
                            </div>
                            <button
                              onClick={() => handleRetirarEquipo(eq)}
                              className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded font-bold"
                            >
                              Retirar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Instalar Equipos en Stock */}
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">Instalar equipo desde vehículo ({equiposDisponibles.length})</h4>
                    <input
                      type="text"
                      placeholder="Buscar por S/N o Marca..."
                      value={searchEquipoTerm}
                      onChange={(e) => setSearchEquipoTerm(e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-800 mb-2"
                    />
                    
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {equiposDisponibles
                        .filter(eq => eq.numeroSerie.toLowerCase().includes(searchEquipoTerm.toLowerCase()) || (eq.marca?.toLowerCase() || '').includes(searchEquipoTerm.toLowerCase()))
                        .slice(0, 5)
                        .map(eq => (
                          <div key={eq.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg text-xs border border-gray-200">
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">{eq.marca} {eq.modelo}</p>
                              <p className="text-[10px] text-gray-500">S/N: {eq.numeroSerie}</p>
                            </div>
                            <button
                              onClick={() => handleInstalarEquipo(eq)}
                              className="px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded font-bold"
                            >
                              Instalar
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Botón de Salida */}
        <button
          onClick={handleFinalizar}
          className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold transition-all text-center shadow-lg"
        >
          Volver a la Ruta de Hoy
        </button>

        {/* Modal de Firma Digital */}
        {showSignatureModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2">
                <h3 className="font-bold text-gray-900 dark:text-white">Firma del Cliente</h3>
                <button
                  onClick={() => setShowSignatureModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="border border-gray-300 dark:border-gray-600 rounded-2xl overflow-hidden bg-gray-50">
                <SignatureCapture
                  onSignatureChange={handleSaveSignature}
                  initialSignature={signatureData}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // PANTALLA PRINCIPAL DEL FORMULARIO DE ENTREGA (UNA SOLA VISTA)
  return (
    <div className="max-w-md mx-auto space-y-6 pb-20 sm:pb-8">
      {/* Botón de volver */}
      <button
        onClick={() => window.history.back()}
        className="flex items-center text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-950 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Volver
      </button>

      {/* Titulo */}
      <div>
        <h1 className="text-2xl font-black text-gray-900 dark:text-white">Nueva Entrega</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Registrar productos y cobro de parada</p>
      </div>

      {/* Sección 1: Cliente */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
        <h2 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center">
          <Users className="h-4 w-4 mr-1.5" />
          1. Cliente
        </h2>

        {clienteIdParam && selectedCliente ? (
          // Vista bloqueada si viene preseleccionado
          <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-900 border border-gray-200/30 rounded-2xl">
            <div className="space-y-1">
              <p className="font-bold text-gray-900 dark:text-white text-base">{selectedCliente.nombre}</p>
              <div className="flex items-center space-x-1.5 text-xs text-gray-500 dark:text-gray-400">
                <MapPin className="h-3 w-3" />
                <span>{selectedCliente.direccion}</span>
              </div>
            </div>
            <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded font-bold">
              Ruta
            </span>
          </div>
        ) : (
          // Selector de cliente si no viene por query param
          <div className="relative">
            {selectedCliente ? (
              <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-900 border border-gray-200/30 rounded-2xl">
                <div className="space-y-1">
                  <p className="font-bold text-gray-900 dark:text-white text-base">{selectedCliente.nombre}</p>
                  <p className="text-xs text-gray-500">{selectedCliente.direccion}</p>
                </div>
                <button
                  onClick={() => setSelectedCliente(null)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={searchClienteTerm}
                  onChange={(e) => {
                    setSearchClienteTerm(e.target.value);
                    setShowClienteDropdown(true);
                  }}
                  onFocus={() => setShowClienteDropdown(true)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                
                {showClienteDropdown && (
                  <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl max-h-60 overflow-y-auto z-40">
                    {clientesFiltrados.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500 text-center">No se encontraron clientes</p>
                    ) : (
                      clientesFiltrados.slice(0, 10).map(c => (
                        <button
                          key={c.id}
                          onClick={() => handleSelectCliente(c)}
                          className="w-full text-left p-3.5 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/30 last:border-b-0"
                        >
                          <p className="font-semibold text-gray-900 dark:text-white text-sm">{c.nombre}</p>
                          <p className="text-xs text-gray-500 truncate">{c.direccion}</p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Notas y Saldo anterior del cliente */}
        {selectedCliente && (
          <div className="grid grid-cols-2 gap-3 pt-2 text-xs">
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-2xl border border-gray-200 dark:border-gray-700">
              <span className="text-gray-400 block mb-0.5">Saldo Anterior</span>
              <span className="font-bold text-gray-900 dark:text-white text-sm">
                ${(selectedCliente.saldoPendiente || 0).toLocaleString()}
              </span>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-2xl border border-gray-200 dark:border-gray-700 max-h-16 overflow-y-auto">
              <span className="text-gray-400 block mb-0.5">Notas / Avisos</span>
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {selectedCliente.observaciones || 'Sin observaciones'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sección 2: Productos */}
      {selectedCliente && (
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4 animate-slideDown">
          <h2 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center">
            <Package className="h-4 w-4 mr-1.5" />
            2. Productos
          </h2>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {productos.map(p => {
              const qty = cantidades[p.id!] || 0;
              return (
                <div key={p.id} className="flex items-center justify-between py-3.5 first:pt-0 last:pb-0">
                  <div className="flex-1 pr-3">
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">{p.nombre}</h3>
                    <p className="text-xs text-gray-500 font-semibold mt-0.5">
                      ${p.precioVenta.toLocaleString()} · Stock: {p.stock !== undefined ? p.stock : 'N/A'}
                    </p>
                  </div>
                  
                  {/* Botones de cantidades adaptados a una sola mano */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => ajustarCantidad(p.id!, -1)}
                      disabled={qty === 0}
                      className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl transition-all disabled:opacity-40"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    
                    <span className="w-8 text-center font-extrabold text-gray-900 dark:text-white text-base">
                      {qty}
                    </span>

                    <button
                      onClick={() => ajustarCantidad(p.id!, 1, p.stock)}
                      className="w-10 h-10 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-sm"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sección 3: Configuración de Pago */}
      {selectedCliente && totalProductos > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4 animate-slideDown">
          <h2 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center">
            <CreditCard className="h-4 w-4 mr-1.5" />
            3. Pago
          </h2>

          {/* Toggle de Tipo de Pago */}
          <div className="flex bg-gray-100 dark:bg-gray-900 p-1.5 rounded-2xl">
            {[
              { id: 'no_pagado', label: 'Fiado (No Pagado)' },
              { id: 'pagado_completo', label: 'Completo' },
              { id: 'pago_parcial', label: 'Parcial' }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setTipoPago(item.id as 'no_pagado' | 'pagado_completo' | 'pago_parcial')}
                className={`flex-1 py-3 text-center rounded-xl text-xs sm:text-sm font-bold transition-all ${
                  tipoPago === item.id 
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Métodos de Pago si hay cobro */}
          {tipoPago !== 'no_pagado' && (
            <div className="space-y-3.5 pt-2 animate-slideDown">
              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Método de Pago
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'efectivo', label: 'Efectivo' },
                    { id: 'transferencia', label: 'Transferencia' },
                    { id: 'tarjeta', label: 'Tarjeta' }
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => setMedioPago(item.id as 'efectivo' | 'transferencia' | 'tarjeta')}
                      className={`py-3.5 rounded-xl text-center text-xs font-bold transition-all border ${
                        medioPago === item.id
                          ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 font-extrabold shadow-sm'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Input de monto parcial */}
              {tipoPago === 'pago_parcial' && (
                <div className="animate-slideDown">
                  <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    Monto pagado hoy ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={totalCobro}
                    value={montoPagoParcial}
                    onChange={(e) => setMontoPagoParcial(Math.min(totalCobro, Number(e.target.value)))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-bold"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sección 4: Observaciones */}
      {selectedCliente && (
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Observaciones de la Entrega (Opcional)
          </label>
          <textarea
            placeholder="Ej: Se dejó el bidón en la cochera, pagará la semana que viene..."
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm h-20"
          />
        </div>
      )}

      {/* Resumen Final & Confirmar */}
      {selectedCliente && totalProductos > 0 && (
        <div className="space-y-4 animate-slideDown">
          {/* Tarjeta de Resumen */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/30 dark:from-blue-900/10 dark:to-blue-950/20 rounded-3xl p-5 border border-blue-200/50 dark:border-blue-900/50 shadow-sm space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Total de productos</span>
              <span className="font-semibold text-gray-900 dark:text-white">${totalProductos.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Saldo anterior</span>
              <span>+${(selectedCliente.saldoPendiente || 0).toLocaleString()}</span>
            </div>
            {montoPagado > 0 && (
              <div className="flex justify-between text-xs text-green-600">
                <span>Monto cobrado</span>
                <span>-${montoPagado.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-blue-200/30 dark:border-blue-800/40 pt-2 font-bold text-base">
              <span className="text-gray-800 dark:text-gray-200">Nuevo Saldo Cliente</span>
              <span className="text-blue-600 dark:text-blue-400">${nuevoSaldo.toLocaleString()}</span>
            </div>
          </div>

          {/* Botón único de confirmación */}
          <button
            onClick={handleConfirmarEntrega}
            disabled={submitting}
            className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-3xl font-bold text-center text-base shadow-lg transition-all flex items-center justify-center space-x-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Confirmando entrega...</span>
              </>
            ) : (
              <span>Confirmar Entrega</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
